import { useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';
import { Dropdown } from 'components/common/dropdown';
import { Form, Switch } from 'components/common/form';

import type { FormApi } from 'informed';
import type { Group } from 'shared-types';

interface Props {
  groups: Pick<
    Group,
    | 'id'
    | 'infoText'
    | 'infoTextEnable'
    | 'infoTextTitle'
    | 'title'
    | 'updatedAt'
  >[];
  onChange: (value: boolean) => void;
}

const HomeHeaderShowInfo = ({ groups, onChange }: Props) => {
  const {
    messages,
    serverConfig: { homePageInfoUpdatedTimestamp },
    user
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const LOCAL_STORAGE_KEY = `vuoHomePageInfoHideTimestamp-${user?.id}`;

  useEffect(() => {
    try {
      const groupInfoUpdatedTimestamp = groups.reduce(
        (value: number | null, group) => {
          const groupUpdatedAt = Date.parse(group.updatedAt as string);
          return !value || groupUpdatedAt > value ? groupUpdatedAt : value;
        },
        null
      );

      const infoUpdatedTimestamp =
        groupInfoUpdatedTimestamp > homePageInfoUpdatedTimestamp
          ? groupInfoUpdatedTimestamp
          : homePageInfoUpdatedTimestamp;

      const hideTimestampString = localStorage.getItem(LOCAL_STORAGE_KEY);
      const hideTimestamp = hideTimestampString
        ? Number(hideTimestampString)
        : null;

      if (hideTimestamp && hideTimestamp > infoUpdatedTimestamp) {
        return;
      } else {
        onChange(true);
        formApi.current?.setValue('homePageShowInfo', true);
      }
    } catch (error) {
      onChange(true);
      formApi.current?.setValue('homePageShowInfo', true);
    }
  }, []);

  const handleShowInfoChange = (value: boolean) => {
    onChange(value);
    try {
      if (value) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      } else {
        localStorage.setItem(LOCAL_STORAGE_KEY, Date.now().toString());
      }
    } catch (error) {
      // do nothing
    }
  };

  return (
    <div className="settings" data-keep-open={true}>
      <Dropdown
        category="light"
        icon="cog"
        label={messages.commonSettings}
        preventDefault={false}
        size="medium"
        styleContentButtons={false}
        tooltip={messages.commonSettings}
      >
        <Form getApi={(api) => (formApi.current = api)}>
          <div className="settings-inner">
            <Switch
              field="homePageShowInfo"
              id="home-page-show-info"
              initialValue={false}
              labelBefore={messages.homePageShowInfo}
              labelStyle={{
                flexGrow: 1,
                fontSize: '1.4rem',
                fontWeight: 600,
                marginRight: 10,
                position: 'relative',
                top: 1
              }}
              onValueChange={handleShowInfoChange}
            />
          </div>
        </Form>
      </Dropdown>

      <style jsx>{`
        .settings-inner {
          min-width: 200px;
          padding: 10px 15px;
        }
      `}</style>
    </div>
  );
};

export default HomeHeaderShowInfo;
